import React, { ChangeEvent, useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useLocation, useNavigate } from "react-router-dom";
import { URL_CONSTANTS } from "src/constants/url.constants";
import { commonLocales } from "src/locales/commonLocales";
import { locales } from "src/locales/indexLocales";

import { cilLockLocked, cilUser } from "@coreui/icons";
import CIcon from "@coreui/icons-react";
import {
  CButton,
  CCard,
  CCardBody,
  CCardGroup,
  CCol,
  CContainer,
  CForm,
  CFormInput,
  CInputGroup,
  CInputGroupText,
  CLink,
  CRow,
} from "@coreui/react";

import PageLoader from "../../components/PageLoader/PageLoader";
import { CONSTANTS } from "../../constants";
import { COMMON_CONSTANTS } from "../../constants/commonConstants";
import { PASSWORD_TYPE_CONSTANTS } from "../../constants/userConstants";
import { useAuth } from "../../context/userContext";
import { AuthValue } from "../../models/layout.models";
import { LoginProps } from "../../models/user.model";
import API_SERVICES from "../../services";
import handleResponse from "../../services/apiConfig/api.response.service";
import commonService from "../../services/common.service";
import commonUtils from "../../utils/common.utils";

function validateEmail(email: string): boolean {
  const pattern: RegExp = COMMON_CONSTANTS.EMAIL_REGEX_VALIDATE_EXPRESSION;
  return pattern.test(email);
}

const Login: React.FC = () => {
  const [formData, setFormData] = useState<LoginProps>({
    username: "",
    password: "",
  });
  const navigate = useNavigate();
  const profile = localStorage.getItem(
    CONSTANTS.LOCAL_STORAGE.PROFILE
  ) as string;
  const [emailError, setEmailError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [formValid, setFormValid] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(
    CONSTANTS.COMMON_CONSTANTS.NO
  );
  const [captchaError, setCaptchaError] = useState(
    CONSTANTS.COMMON_CONSTANTS.NO
  );

  const { login } = useAuth() as AuthValue;
  const { pathname } = useLocation();

  useEffect(() => {
    if (JSON.parse(profile)) {
      navigate("/admin/dashboard", { replace: true });
    }
  }, [pathname, navigate]);

  const handleCaptchaChange = (token: string | null) => {
    if (token) {
      setCaptchaVerified(CONSTANTS.COMMON_CONSTANTS.YES);
      setCaptchaError(CONSTANTS.COMMON_CONSTANTS.NO);
    } else {
      setCaptchaVerified(CONSTANTS.COMMON_CONSTANTS.NO);
    }
  };

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData(prevState => {
      return {
        ...prevState,
        [name]: value?.trim(),
      };
    });

    if (name === COMMON_CONSTANTS.LABEL_NAME && !validateEmail(value?.trim())) {
      setEmailError(true);
    } else {
      setEmailError(false);
    }
  };

  const handleLogin = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormValid(false);
    const form = event.currentTarget;
    if (!form?.checkValidity()) {
      setFormValid(true);
      return;
    }
    if (!validateEmail(formData?.username?.trim())) {
      setEmailError(true);
      return;
    }

    if (!captchaVerified) {
      setCaptchaError(CONSTANTS.COMMON_CONSTANTS.YES);
      return;
    }

    setEmailError(false);
    setLoading(true);
    LoginPage();
  };

  const LoginPage = () => {
    API_SERVICES.USER.ADMIN_LOGIN(formData).then(data => {
      const responseData = handleResponse(data);
      if (responseData?.ok) {
        const tokens: { [key: string]: string } = {};
        const accessToken = responseData?.data?.token;
        const refreshToken = responseData?.data?.refresh_token;
        tokens[CONSTANTS.LOCAL_STORAGE.ACCESS_TOKEN] = accessToken;
        tokens[CONSTANTS.LOCAL_STORAGE.REFRESH_TOKEN] = refreshToken;
        const userdata = responseData?.data?.userdata;
        commonService.setLocalStorage(tokens);
        login({ ...userdata, accessToken });
        if (responseData.status === CONSTANTS.HTTP_STATUS.SUCCESS) {
          commonUtils.toastService(
            PASSWORD_TYPE_CONSTANTS.LOGIN_SUCCESS_MESSAGE,
            CONSTANTS.TOAST_TYPE.SUCCESS
          );
        }
      } else {
        commonUtils.toastService(
          responseData?.data?.message,
          CONSTANTS.TOAST_TYPE.ERROR
        );
      }
      setLoading(false);
    });
  };

  return (
    <>
      <PageLoader isLoading={loading} />
      <div className="bg-light min-vh-100 d-flex flex-row align-items-center">
        <CContainer>
          <CRow className="justify-content-center">
            <CCol md={7} lg={6} xl={5} className="p-0">
              <div className="dd-header p-0 position-relative">
                <div className="wrapLogo">
                  <img
                    className="dd-logo"
                    src={COMMON_CONSTANTS.LOGIN_FORM_LOGO}
                    alt="dental dynamic staffing"
                  />
                </div>
              </div>
              <CCardGroup>
                <CCard className="p-4 loginForm">
                  <CCardBody>
                    <CForm
                      noValidate
                      validated={formValid}
                      onSubmit={handleLogin}
                    >
                      <h4 className="fw-bold pt-2">
                        {commonLocales.loginHeading}
                      </h4>
                      <p className="text-medium-emphasis">
                        {commonLocales.loginSubHeading}
                      </p>
                      <CInputGroup className="mb-3 dd-input-border">
                        <CInputGroupText>
                          <CIcon icon={cilUser} />
                        </CInputGroupText>
                        <CFormInput
                          placeholder="Username"
                          autoComplete="username"
                          name="username"
                          feedbackInvalid="Please provide a valid email."
                          required
                          onChange={handleOnChange}
                          value={formData?.username}
                          invalid={emailError}
                        />
                      </CInputGroup>
                      <CInputGroup className="mb-4 dd-input-border">
                        <CInputGroupText>
                          <CIcon icon={cilLockLocked} />
                        </CInputGroupText>
                        <CFormInput
                          type="password"
                          placeholder="Password"
                          name="password"
                          autoComplete="current-password"
                          feedbackInvalid="Please provide a valid password."
                          required
                          onChange={handleOnChange}
                          value={formData?.password}
                        />
                      </CInputGroup>

                      <CRow className="mb-4">
                        <CCol className="d-flex justify-content-end dd-password-link">
                          <CLink href={URL_CONSTANTS.FORGOT_PASSWORD}>
                            {locales.forgotPassword}
                          </CLink>
                        </CCol>
                      </CRow>
                      <CRow className="mb-3">
                        <CCol className="d-flex justify-content-center">
                          <ReCAPTCHA
                            sitekey={
                              process.env.REACT_APP_RECAPTCHA_SITE_KEY as string
                            }
                            onChange={handleCaptchaChange}
                          />
                        </CCol>
                      </CRow>
                      {captchaError && (
                        <p className="text-danger text-center">
                          {commonLocales.recaptchaValidationError}
                        </p>
                      )}
                      <CRow>
                        <CCol className="d-flex justify-content-end">
                          <div>
                            <CButton
                              className="px-4 dd-login-button"
                              type="submit"
                              disabled={!captchaVerified}
                            >
                              Login
                            </CButton>
                          </div>
                        </CCol>
                      </CRow>
                    </CForm>
                  </CCardBody>
                </CCard>
              </CCardGroup>
            </CCol>
          </CRow>
        </CContainer>
      </div>
    </>
  );
};

export default Login;
